import { Component, Inject, OnDestroy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { SubscriptionService } from "src/app/core/service/subscription.service";
import { UserCertificateDTO } from "src/app/data/schema/UserCourse/userCertificate/UserCertificateDTO";
import { UserCertificateQueryParamsDTO } from "src/app/data/schema/UserCourse/userCertificate/UserCertificateQueryParamsDTO";
import { ApiUserCertificatesService } from "src/app/data/service/userCourses/api-user-certificates.service";
import { ActionsTypes, ConfirmDialogComponent, ConfirmDialogData } from "../confirm-dialog/confirm-dialog.component";
import {
    ShareSocialMediasDialogComponent,
    SocialMediaShareOpts
} from "../share-social-medias-dialog/share-social-medias-dialog.component";
import enUS from "./i18n/en-US.json";
import esES from "./i18n/es-ES.json";
import ptBR from "./i18n/pt-BR.json";

@Component({
    selector: "app-certificate-viewer",
    templateUrl: "./certificate-viewer.component.html",
    styleUrls: ["./certificate-viewer.component.scss"]
})
export class CertificateViewerComponent implements OnDestroy {
    unsubscribeAll: Subject<any>;

    certificate: UserCertificateDTO;
    isShareble = false;
    query: UserCertificateQueryParamsDTO;
    publicImageUrl: string;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: UserCertificateQueryParamsDTO,
        public spinner: NgxSpinnerService,
        private userCertificateService: ApiUserCertificatesService,
        private translate: TranslateService,
        private dialog: MatDialog,
        private subscriptionService: SubscriptionService,
        private snackBar: MatSnackBar,
        private dialogRef: MatDialogRef<CertificateViewerComponent>
    ) {
        this.unsubscribeAll = new Subject();
        this.translate.setTranslation("pt-BR", ptBR, true);
        this.translate.setTranslation("en-US", enUS, true);
        this.translate.setTranslation("es-ES", esES, true);

        this.query = data;
        this.loadCertificate();
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.complete();
        this.unsubscribeAll.unsubscribe();
    }

    loadCertificate(): void {
        this.spinner.show();

        this.userCertificateService
            .list(this.query)
            .pipe(
                takeUntil(this.unsubscribeAll),
                finalize(() => this.spinner.hide())
            )
            .subscribe((res) => {
                this.certificate = res.data[0];

                if (!this.certificate) {
                    this.snackBar.open(this.translate.instant("CERTIFICATE_VIEWER.NOT_FOUND"), "OK", {
                        duration: 5000,
                        panelClass: "msg-error"
                    });
                    this.dialogRef.close();
                }
                this.isShareble = this.certificate?.images[0]?.publicUrl != null;
                if (this.isShareble)
                    this.publicImageUrl = encodeURIComponent(
                        `https://${this.subscriptionService.currentSubscription.host}/app/${this.certificate.images[0].publicUrl}`
                    );
            });
    }

    download(): void {
        window.open(this.certificate.pdfUrl, "_blank");
    }

    share(): void {
        if (this.isShareble) {
            this.openShareOptions();
            return;
        }

        const cfg: MatDialogConfig<ConfirmDialogData> = {
            data: {
                msg: this.translate.instant("CERTIFICATE_VIEWER.SHARE.WARN.MSG"),
                title: this.translate.instant("CERTIFICATE_VIEWER.SHARE.WARN.TITLE"),
                mandatoryConfirmationPhrase: false,
                type: ActionsTypes.yes_no
            }
        };

        this.dialog
            .open(ConfirmDialogComponent, cfg)
            .afterClosed()
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((res) => {
                if (!res) return;
                this.spinner.show();
                this.userCertificateService
                    .publicCertificateImage(this.certificate.id)
                    .pipe(finalize(() => this.spinner.hide()))
                    .subscribe((url) => {
                        this.publicImageUrl = encodeURIComponent(
                            `https://${this.subscriptionService.currentSubscription.host}/app/${url}`
                        );

                        this.isShareble = true;

                        this.openShareOptions();
                    });
            });
    }

    openShareOptions(): void {
        const cfg: MatDialogConfig<SocialMediaShareOpts> = {
            data: {
                url: this.publicImageUrl,
                text: this.translate.instant("CERTIFICATE_VIEWER.SHARE.TEXT", {
                    course: this.certificate.course.titulo
                }),
                title: this.translate.instant("CERTIFICATE_VIEWER.SHARE.LABEL")
            },
            maxWidth: "600px",
            width: "99vw"
        };

        this.dialog.open(ShareSocialMediasDialogComponent, cfg);
    }
}
